import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { removeAttachment } from 'utils/attachmentOperations'
import { restrictiveListTypes } from 'utils/constants/DueDiligenceRelations'
import AddAttachments from 'components/AddAttachments'
import { useSelector } from 'react-redux'
import { hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'
import AttachmentsList from '../../../AttachmentsList'

export default function QuestionSeventeen(props) {
  const { questionData, saveResponses, formOwner } = props
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const initialData = {
    process_number: '',
    restriction_type: 'default',
    other_restrictions: '',
    attachments: [],
  }

  const initialErrors = {
    process_number: false,
    restriction_type: false,
    other_restrictions: false,
    attachments: false,
  }

  const initializeFormState = (data) => {
    return {
      hasRestrictions: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [attachmentsToRemove, setAttachmentsToRemove] = useState([])

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = event.target.value
    setActivityData(newActivityData)
  }

  const handleSelectChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.restriction_type = event.target.value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasRestrictions = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const hasRestrictions = () => {
    return !(formState.items.length === 0)
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (key === 'attachments' && activityData[key].length === 0) {
        newActivityErrors[key] = true
        return
      }
      if (key === 'restriction_type' && activityData[key] === 'default') {
        newActivityErrors[key] = true
        return
      }
      if (
        key === 'other_restrictions' &&
        activityData[key] === '' &&
        activityData.restriction_type === 'other'
      ) {
        newActivityErrors[key] = true
        return
      }
      if (activityData[key] === '' && key !== 'other_restrictions') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveActivity = async (position) => {
    const newFormState = cloneDeep(formState)
    const attachmentsToDelete = newFormState.items[position].attachments

    setAttachmentsToRemove([...attachmentsToRemove, ...attachmentsToDelete])

    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const handleRemoveExcludedAttachments = async (attachments) => {
    Promise.all(
      attachments.map((attachment) => removeAttachment(attachment.uuid, token)),
    ).then(() => {
      setAttachmentsToRemove([])
    })
  }

  const handleUpdateAttachments = (attachments) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.attachments = attachments
    setActivityData(newActivityData)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (
      formState.items.length > 0 &&
      formState.hasRestrictions === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (!skipValidation && formState.hasRestrictions && !hasRestrictions()) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'restrictive_list',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(async () => {
          await handleRemoveExcludedAttachments(attachmentsToRemove)
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Lista restritiva: </span>
          {activityInfo.restriction_type === 'other'
            ? activityInfo.other_restrictions
            : restrictiveListTypes[activityInfo.restriction_type]}
        </p>
        <p>
          <span>Número do processo: </span>
          {activityInfo.process_number}
        </p>
        {activityInfo.attachments.length > 0 ? (
          <AttachmentsList attachments={activityInfo.attachments} />
        ) : null}
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasRestrictions !== questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasRestrictions === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 17</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Já esteve envolvido em alguma lista restritiva, tais como:
              <ul className={classes.optionsList}>
                <li>Cadastro de Expulsões da Administração Federal - CEAF</li>
                <li>Cadastro Nacional de Empresas Punidas - CNEP</li>
                <li>Conselho de Segurança das Nações Unidas - UNSC</li>
                <li>Office of Foreign Assets Control - OFAC</li>
                <li>Interpol</li>
                <li>Empresas Inidôneas e Suspensas - CEIS</li>
                <li>Mandatos de Prisão (PF)</li>
              </ul>
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasRestrictions ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasRestrictions ? (
            <div className={classes.formFields}>
              <Select
                onChange={handleSelectChange}
                value={activityData.restriction_type}
                error={activityErrors.restriction_type}
                size="small"
              >
                <MenuItem value="default" disabled>
                  Selecione o tipo da lista restritiva
                </MenuItem>
                {Object.keys(restrictiveListTypes).map((key) => (
                  <MenuItem key={key} value={key}>
                    {restrictiveListTypes[key]}
                  </MenuItem>
                ))}
              </Select>
              {activityData.restriction_type === 'other' ? (
                <TextField
                  id="other_restrictions"
                  label="Orgão da restrição"
                  onChange={handleFieldChange}
                  required
                  fullWidth
                  value={activityData.other_restrictions}
                  error={activityErrors.other_restrictions}
                  size="small"
                />
              ) : null}
              <TextField
                id="process_number"
                label="Número do processo"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.process_number}
                error={activityErrors.process_number}
                size="small"
              />

              <AddAttachments
                targetFilename={`${formOwner?.prospect?.person?.cpf}-Comprovante_envolvimento_listas_restritivas`}
                title="Anexe um resumo da ocorrência, bem como o processo administrativo, se houver: "
                attachments={activityData.attachments}
                updateAttachments={handleUpdateAttachments}
                error={activityErrors.attachments}
              />

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map(
              (activity, index) => (
                <AnswerCard
                  key={activity.key}
                  index={index}
                  handleRemove={handleRemoveActivity}
                  mustRemove={formState.hasRestrictions === false}
                >
                  {buildActivity(activity)}
                </AnswerCard>
              ),
            )}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasRestrictions &&
              formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
